import React, { useContext, useState } from 'react'

import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Tooltip from '@mui/material/Tooltip'
import { NoSsr, useMediaQuery, useTheme } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'

import Link from '../Link'
import Button from '../Button'
import AvatarIconAccount from '../../../public/images/icons/myAccount/avatar_user.svg'
import Cart from '../../../public/images/icons/myAccount/cart.svg'
import MessagingIcon from '../../../public/images/icons/messaging.svg'
import SETTINGS from '@/src/settings'
import MenuPopupState from './MenuPopupState'
import ProfileContext from '@/components/Context/ProfileContext'
import Skeleton from '@mui/material/Skeleton'
import useUnreadMessages from '@/lib/hook/useUnreadMessages'
import useDisputeActionCount from '@/lib/hook/useDisputeActionCount'
import FloatingToolbox, { useRecentProductsCount } from '@/components/Toolbox/FloatingToolbox'

type Props = {
  authenticate: boolean
}

const IconMenu: React.FC<Props> = ({ authenticate }) => {
  const theme = useTheme()
  const downSM = useMediaQuery(theme.breakpoints.down('sm'))
  const [historyOpen, setHistoryOpen] = useState(false)
  const recentCount = useRecentProductsCount()

  const {
    loadingCounterProducts,
    loadingRefetchBasket,
    isAuthenticated,
    dataCounterProducts,
    profile,
  } = useContext(ProfileContext)

  const canMessage = profile?.canMessage !== false
  const { unreadCount, unreadOrders } = useUnreadMessages({ isAuthenticated, enabled: canMessage })
  // Litiges en attente d'action du client -> badge sur l'icone compte.
  const disputeActionCount = useDisputeActionCount(isAuthenticated)

  const messagingTooltip = null

  const multi = [
    ...(canMessage
      ? [
          {
            _id: 0,
            svg: MessagingIcon,
            sizeSvg: downSM ? 20 : 30,
            numberBadge: unreadCount,
            tooltip: messagingTooltip,
            route: authenticate ? 'Messages' : 'LoginRedirect',
          },
        ]
      : []),
    {
      _id: 1,
      svg: AvatarIconAccount,
      sizeSvg: downSM ? 20 : 30,
      numberBadge: authenticate ? disputeActionCount : 0,
      component: authenticate ? MenuPopupState : null,
      route: authenticate ? 'Dashboard' : 'LoginRedirect',
      testId: authenticate ? 'Dashboard' : 'LoginRedirect',
    },
    {
      _id: 2,
      svg: Cart,
      itemId: 'cart-button-header',
      sizeSvg: downSM ? 20 : 30,
      numberBadge: dataCounterProducts?.totalQuantity ?? 0,
      handleLoader: ({ loadingRefetchBasket, loadingCounterProducts }) =>
        loadingRefetchBasket || loadingCounterProducts,
      route: authenticate ? 'Basket' : 'LoginRedirect',
    },
  ]

  const sizeSvg = downSM ? 16 : 24

  return (
    <NoSsr>
      {SETTINGS.feature.devis && (
        <div>
          <Button color={'primary'}>{t({ id: 'web_demander_devis' })}</Button>
        </div>
      )}
      <div className="flex flex-row items-center justify-end">
        {multi.map(({ component: Component, svg: Svg, tooltip, ...m }) => {
          const iconSize = downSM ? 32 : 40
          const iconButton = (
            <Badge
              badgeContent={m.numberBadge}
              color="error"
              overlap={'circular'}
              variant={downSM ? 'dot' : 'standard'}
            >
              <IconButton id={m.itemId} sx={{ cursor: 'pointer', width: iconSize, height: iconSize }}>
                {m?.handleLoader &&
                m.handleLoader({ loadingRefetchBasket, loadingCounterProducts }) ? (
                  // Skeleton (placeholder statique) plutot qu'un spinner : transition
                  // plus douce pendant le chargement du compteur panier.
                  <Skeleton variant="circular" width={sizeSvg} height={sizeSvg} animation="wave" />
                ) : (
                  <Svg width={m.sizeSvg} height={m.sizeSvg} fill={theme.palette.primary.main} />
                )}
              </IconButton>
            </Badge>
          )

          const wrappedIcon = tooltip ? (
            <Tooltip title={tooltip} placement="bottom-end" arrow>
              <span>{iconButton}</span>
            </Tooltip>
          ) : (
            iconButton
          )

          return (
            <Link route={m.route} naked key={m._id} data-testid={m.testId}>
              <div className={downSM ? 'm-0.5' : 'm-2'}>
                {Component ? (
                  <Component {...m} authenticate={authenticate}>
                    {wrappedIcon}
                  </Component>
                ) : (
                  <>{wrappedIcon}</>
                )}
              </div>
            </Link>
          )
        })}

        {/* History button — after cart */}
        {recentCount > 0 && (
          <div className={`${downSM ? 'm-0.5' : 'm-2'} relative`}>
            <Badge
              badgeContent={recentCount}
              color="error"
              overlap="circular"
              variant={downSM ? 'dot' : 'standard'}
            >
              <IconButton
                onClick={() => setHistoryOpen((o) => !o)}
                sx={{ cursor: 'pointer', width: downSM ? 32 : 40, height: downSM ? 32 : 40 }}
                aria-label="Historique produits"
              >
                <HistoryIcon
                  sx={{
                    width: downSM ? 20 : 30,
                    height: downSM ? 20 : 30,
                    color: theme.palette.primary.main,
                  }}
                />
              </IconButton>
            </Badge>
            <FloatingToolbox open={historyOpen} onClose={() => setHistoryOpen(false)} />
          </div>
        )}
      </div>
    </NoSsr>
  )
}
export default IconMenu
